import type { Stripe } from '@stripe/stripe-js/types/stripe-js/stripe';
import Vue, { markRaw } from 'vue';
import type { VNode, CreateElement } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import type { StripeElements } from '@stripe/stripe-js/types/stripe-js/elements-group';
import { Component, Prop, Watch } from 'vue-property-decorator';
import type { StripeCardCvcElement, StripeCardNumberElement, StripeCardExpiryElement, StripeCardCvcElementChangeEvent, StripeCardNumberElementChangeEvent, StripeCardExpiryElementChangeEvent } from '@stripe/stripe-js/types/stripe-js/elements/';
import { getCssVarValue } from '@cognitoforms/utils/css-helper';
import { isCardEntryPermitted } from 'src/utilities/plan-and-billing-zone';

@Component
export default class BillingCardElement extends Vue {
	stripe: Stripe = null;
	elements: StripeElements = null;
	cardNumberElement: StripeCardNumberElement = null;
	cardCvcElement: StripeCardCvcElement = null;
	cardExpiryElement: StripeCardExpiryElement = null;
	initializePromise: Promise<StripeElements> = null;

	// Empty billing fields represent no change in information, this object is used to determine whether the card is modified from its original state.
	emptyFields: object = {
		cardNumber: true,
		cardCvc: true,
		cardExpiry: true
	};

	@Prop({ type: String, required: true })
	publishableKey: string;

	@Prop({ type: Boolean, required: true })
	cardOnFile: boolean;

	@Prop({ type: Object, required: true })
	existingCardInfo;

	@Watch('cardOnFile')
	onCardOnFileChanged(newValue: boolean, oldValue: boolean) {
		this.onExternalCardChange();
	}

	/**
	 * Whether this document is allowed to collect card details at all. Refusing here keeps Stripe.js and the
	 * card fields off any document the server did not serve under the billing zone's policy, which is the one
	 * place in the application that mounts them.
	 */
	get cardEntryPermitted(): boolean {
		return isCardEntryPermitted();
	}

	colorTextPrimary = getCssVarValue('--text-primary');
	colorTextPlaceholder = getCssVarValue('--text-placeholder');

	get cardModified() {
		return !this.emptyFields['cardNumber'] || !this.emptyFields['cardCvc'] || !this.emptyFields['cardExpiry'];
	}

	mounted() {
		if (!this.cardEntryPermitted)
			return;

		this.initialize();
	}

	render(h: CreateElement): VNode {
		if (!this.cardEntryPermitted)
			return h('div');

		return h('div', [
			h('div', { class: 'billing-field-container flex items-center gap-2 px-2 border border-solid border-input w-full leading-default mb-2' }, [
				h('div', { ref: 'Card', class: 'card-number w-full' }),
				h('inline', { attrs: { svg: 'credit-card', class: 'text-icon-subtle' } })
			]),
			h('div', { class: 'flex gap-2' }, [
				h('div', { class: 'billing-field-container flex items-center gap-2 px-2 border border-solid border-input w-full leading-default mb-2' }, [
					h('div', { class: 'card-expiry w-full' }),
					h('inline', { attrs: { svg: 'date', class: 'text-icon-subtle' } })
				]),
				h('div', { class: 'billing-field-container flex items-center gap-2 px-2 border border-solid border-input w-full leading-default mb-2' }, [
					h('div', { class: 'card-cvc w-full' }),
					h('inline', { attrs: { svg: 'lock', class: 'text-icon-subtle' } })
				])
			])
		]);
	}

	async getStripeInstance(): Promise<Stripe> {
		if (this.stripe) {
			return this.stripe;
		}
		else if (window.Stripe) {
			this.stripe = window.Stripe(this.publishableKey);
			return this.stripe;
		}

		const stripe = await loadStripe(this.publishableKey);
		if (!stripe) {
			throw new Error('Failed to load Stripe.js.');
		}

		markRaw(stripe);
		this.stripe = stripe;
		return this.stripe;
	}

	updateAllFields(data) {
		this.cardCvcElement.update(data);
		this.cardNumberElement.update(data);
		this.cardExpiryElement.update(data);
	}

	prefillData() {
		if (this.cardOnFile && !this.cardModified) {
			this.updateAllFields({ style: { base: { '::placeholder': { color: this.colorTextPrimary } } } });
			this.cardNumberElement.update({ placeholder: this.existingCardInfo.cardNumberPlaceholder });
			this.cardExpiryElement.update({ placeholder: this.existingCardInfo.cardExpiryPlaceholder });
			this.cardCvcElement.update({ placeholder: this.existingCardInfo.cardCvcPlaceholder });
		}
	}

	onExternalCardChange() {
		if (!this.elements)
			return;

		this.updateAllFields({ style: { base: { '::placeholder': { color: this.cardOnFile ? this.colorTextPrimary : this.colorTextPlaceholder } } } });
		this.cardNumberElement.update({ placeholder: this.cardOnFile ? this.existingCardInfo.cardNumberPlaceholder : 'Credit Card Number' });
		this.cardExpiryElement.update({ placeholder: this.cardOnFile ? this.existingCardInfo.cardExpiryPlaceholder : 'MM/YY' });
		this.cardCvcElement.update({ placeholder: this.cardOnFile ? this.existingCardInfo.cardCvcPlaceholder : 'CVV' });
	}

	async handleCardChange(e: StripeCardNumberElementChangeEvent | StripeCardCvcElementChangeEvent | StripeCardExpiryElementChangeEvent) {
		await this.$nextTick();
		this.emptyFields[e.elementType] = e.empty;
		this.$emit('change', this.cardModified);
	}

	handleElementFocus() {
		if (this.cardOnFile) {
			this.updateAllFields({ style: { base: { '::placeholder': { color: this.colorTextPlaceholder } } } });
			this.cardNumberElement.update({ placeholder: 'Credit Card Number' });
			this.cardExpiryElement.update({ placeholder: 'MM/YY' });
			this.cardCvcElement.update({ placeholder: 'CVV' });
		}
	}

	async initialize(): Promise<StripeElements> {
		if (!this.cardEntryPermitted)
			return null;

		if (this.elements)
			return this.elements;

		const stripe = await this.getStripeInstance();

		const initializePromise = new Promise<StripeElements>((resolve, reject) => {
			try {
				const stripeStyle = {
					base: {
						font: 'system-ui,-apple-system,blinkmacsystemfont,"Segoe UI",roboto,oxygen-sans,ubuntu,cantarell,"Helvetica Neue",arial,sans-serif',
						fontSize: '14px',
						color: this.colorTextPrimary,
						'::placeholder': {
							color: this.colorTextPlaceholder
						}
					}
				};

				stripeStyle['invalid'] = stripeStyle.base;

				const elements = stripe.elements();

				this.cardNumberElement = elements.create('cardNumber', {
					style: stripeStyle,
					placeholder: this.cardOnFile ? this.existingCardInfo.cardNumberPlaceholder : 'Credit Card Number'
				});

				this.cardExpiryElement = elements.create('cardExpiry', {
					style: stripeStyle,
					placeholder: this.cardOnFile ? this.existingCardInfo.cardExpiryPlaceholder : 'MM/YY'
				});

				this.cardCvcElement = elements.create('cardCvc', {
					style: stripeStyle,
					placeholder: this.cardOnFile ? this.existingCardInfo.cardCvcPlaceholder : 'CVV'
				});

				this.$nextTick(() => {
					const cardNumberContainer = this.$el.querySelector('.card-number') as HTMLElement;
					const cardExpiryContainer = this.$el.querySelector('.card-expiry') as HTMLElement;
					const cardCvcContainer = this.$el.querySelector('.card-cvc') as HTMLElement;

					this.cardNumberElement.mount(cardNumberContainer);
					this.cardExpiryElement.mount(cardExpiryContainer);
					this.cardCvcElement.mount(cardCvcContainer);
				});

				[this.cardCvcElement, this.cardExpiryElement, this.cardNumberElement].forEach((e) => {
					e.on('change', this.handleCardChange);
					e.on('focus', this.handleElementFocus);
					e.on('ready', this.prefillData);
					e.on('blur', this.prefillData);
				});

				markRaw(elements);

				this.elements = elements;

				this.initializePromise = null;

				resolve(elements);
			}
			catch (error) {
				this.initializePromise = null;
				throw error;
			}
		});

		markRaw(initializePromise);
		this.initializePromise = initializePromise;

		return this.initializePromise;
	}
}
